import { Component, OnDestroy, Input, ViewChild, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActionPacksMakeOrder } from '@app/certificates/packs/packs.reducer';
import { Store } from '@ngrx/store';
import { IPackItem, IPackData } from '@app/certificates/packs/packs';
import { InfoService } from '@app/core/info/info.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { InvoiceFormComponent } from '@app/certificates/packs/invoice-form/invoice-form.component';
import { environment } from '@env/environment';
import { nomenclatureStoreSelector } from '@app/core/nomenclature/nomenclature.reducer';
import { takeUntil } from 'rxjs/operators';
import { ActionNomenclatureInitialize } from '@app/core/nomenclature/nomenclature.actions';
import { ICountryCode, IPostCode } from '@app/core/nomenclature/nomenclature';
import { NomenclatureService } from '@app/core/nomenclature/nomenclature.service';
import { selectorAuth, SessionStorageService } from '@app/core';
import { ICompanyDataAsPayer, IProfilePlan, IUserData } from '@app/profile/main/details';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmComponent } from '@app/core/info/confirm/confirm.component';
import { IConfirmData } from '@app/core/info/confirm/confirm';
import { profilePlanSelector } from '@app/profile/profile-remaining-count/profile-plan.reducer';

@Component({
  selector: 'cloud-pack-order',
  templateUrl: './pack-order.component.html',
  styleUrls: ['./pack-order.component.scss'],
})
export class PackOrderComponent implements OnInit, OnDestroy {
  @Input() packData: IPackData;
  @Input() selectedPack: IPackItem;

  countryCodes: ICountryCode[];
  postCodes: IPostCode[];

  @ViewChild('invoiceFormComponent') invoiceFormComponent: InvoiceFormComponent;

  private unsubscribe$: Subject<void> = new Subject<void>();
  private confirmDialog: MatDialogRef<ConfirmComponent>;


  loadingNomenclature = true;
  checked = false;
  userData: IUserData;
  sendInvoiceByEmail = false;

  // profile plan data (same source as ProfileRemainingCountComponent)
  profilePlan: IProfilePlan;
  isInfinitePlan: boolean;
  packageRemainingCount: number;

  readonly dateTimeFormat = environment.dateTimeFormat;
  readonly dateFormat = environment.dateFormat;

  constructor(
    private translateService: TranslateService,
    private store: Store<any>,
    private info: InfoService,
    private nomenclatureService: NomenclatureService,
    private sessionStorageService: SessionStorageService,
    private dialog: MatDialog,
  ) { }
  ngOnInit() {
    this.store
      .select(selectorAuth)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (res.userData) {
          this.userData = res.userData;
          if (this.userData == undefined) {
            this.userData = this.sessionStorageService.getItem("loginEventData")
          }
        }
      });

    // subscribe to profile plan from NgRx store
    this.store
      .select(profilePlanSelector)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((profilePlan: IProfilePlan) => {
        if (profilePlan) {
          this.profilePlan = profilePlan;
          this.isInfinitePlan = profilePlan.remainingCount === -1;
          this.packageRemainingCount = profilePlan.remainingCount;
        } else {
          // if not loaded yet, trigger load (same as ProfileRemainingCountComponent)
          this.store.dispatch({ type: '[ProfilePlan] Get' });
        }
      });
    this.store.select(nomenclatureStoreSelector).pipe(takeUntil(this.unsubscribe$))
      .subscribe(selectionData => {
        if (selectionData) {
          this.countryCodes = selectionData.countryCodes;
          this.loadingNomenclature = false;
        } else {
          this.nomenclatureService.get().subscribe(response => {
            this.store.dispatch(new ActionNomenclatureInitialize({ nomenclature: response.data }));
          });
        }
      });

  }

  makeOrder() {

    if(this.profilePlan?.packageData?.name[this.translateService.currentLang] !== this.translateService.instant('free')) {
      this.info.dismissAll();

          const datePipe = new DatePipe(this.translateService.currentLang === 'bg' ? 'bg-BG' : 'en-US');
          const validToFormatted = this.profilePlan?.packageEndDate
            ? datePipe.transform(this.profilePlan.packageEndDate, this.dateFormat)
            : '';

          this.confirmDialog = this.dialog.open(
            ConfirmComponent,
            {
              data: {
                icon: null,
                title: this.translateService.instant('manageTexts.dialogTexts.confirmPackOrderTitle'),
                question: this.translateService.instant('manageTexts.dialogTexts.confirmPackOrderQuestion',
                      {
                        planType: this.profilePlan?.packageData?.name[this.translateService.currentLang],
                        validTo: validToFormatted
                      }
                ),
                confirmTxt: this.translateService.instant('common.confirm'),
                cancelTxt: this.translateService.instant('common.cancel'),
                confirmAction: () => this.onConfirmMakeOrder()
              } as IConfirmData
            });

          this.confirmDialog.afterClosed().subscribe(() => {
            // No additional handling needed here; closing without confirm just cancels.
          });
    } else {
      this.onConfirmMakeOrder();
    }
  }

  private onConfirmMakeOrder() {
    if (this.confirmDialog) {
      this.confirmDialog.close(true);
    }

    let buyer = null;

    if (this.invoiceFormComponent) {
      // this.invoiceFormComponent.cityChecked = true;

      buyer = JSON.parse(JSON.stringify(this.invoiceFormComponent.buyer));
      this.info.endWait();
      // This section marks the component as dirty and touched
      // In order to mark the invalid inputs
      const controls = this.invoiceFormComponent.form.controls;
      const controlKeys = Object.keys(controls);

      controlKeys.forEach((key: string) => {
        controls[key].markAsDirty();
        controls[key].markAsTouched();
      });

      if (this.invoiceFormComponent.form.invalid) {
        this.info.error(
          this.translateService.instant('packsTexts.invalidInvoiceForm')
        );
        return;
      }

    }

    if (buyer.idType === 'ЕГН') {
      buyer.idType = 'E';
    } else if (buyer.idType === 'ЛНЧ') {
      buyer.idType = 'F';
    }

    let companyPayerData: ICompanyDataAsPayer = null;

    if(!this.invoiceFormComponent.isSelectedClientIndividual()) {
      companyPayerData = this.invoiceFormComponent.companyData;
    } 

    this.store.dispatch(new ActionPacksMakeOrder({
      packId: this.selectedPack.id,
      buyer,
      origin: "CLIENT_PORTAL",
      companyPayerData: companyPayerData
    }));
  }

  get lang() {
    return this.translateService.currentLang;
  }

  get validityText() {
    if (this.selectedPack.validityPeriod > -1) {
      if (this.selectedPack.validityPeriod === 1) {
        return this.selectedPack.validityPeriod + ' ' + this.translateService.instant('packsTexts.month');
      } else {
        return this.translateService.instant('packsTexts.months', { period: this.selectedPack.validityPeriod });
      }
    } else {
      return this.translateService.instant('packsTexts.unlimited');
    }
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get hasClientMasterRecord() {
    if (this.userData == undefined) {
      this.userData = this.sessionStorageService.getItem("loginEventData")
    }
    return this.userData.cmrId !== null;
  }
  onCheckedEmail(event) {
    this.sendInvoiceByEmail = event;
  }

}
